import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { api } from '../../api/apiService';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

function AttendanceReport() {
    const [view, setView] = useState('month');
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // For monthly data
    const fetchMonthlyData = async (month, year) => {
        const response = await api.get(`/reports/charts/full-month?month=${month}&year=${year}`);
        return response.data?.data || { labels: [], datasets: [] }; // nested payload
    };

    // For weekly data  
    const fetchWeeklyData = async (weekStart) => {
        const response = await api.get(`/reports/charts/week?week_start=${weekStart}`);
        return response.data?.data || { labels: [], datasets: [] }; // nested payload
    };

    // Update data when view changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                let data;
                const currentDate = new Date();
                
                if (view === 'month') {
                    const month = currentDate.getMonth() + 1;
                    const year = currentDate.getFullYear();
                    data = await fetchMonthlyData(month, year);
                } else {
                    // For weekly data, we'll use the current week start (Monday)
                    const today = new Date();
                    const dayOfWeek = today.getDay();
                    const monday = new Date(today);
                    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
                    const weekStart = monday.toISOString().split('T')[0];
                    data = await fetchWeeklyData(weekStart);
                }
                
                setChartData(data);
            } catch (err) {
                setError('Failed to fetch attendance data');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [view]);

    // Use the data directly from the API response, with fallback for empty state
    const data = {
        labels: chartData.labels || [],
        datasets: chartData.datasets || []
    };

    const options = {
        responsive: true,
        plugins: {
            title: {
                display: true,
                text: 'Attendance Report',
                font: { size: 18 }
            },
            legend: {
                position: 'top'
            },
            datalabels: {
                color: 'white',
                anchor: 'center',
                align: 'center',
                font: { weight: 'bold' },
                formatter: (value) => value
            }
        },
        scales: {
            x: {
                stacked: true,
                title: { display: true, text: 'Day' }
            },
            y: {
                stacked: true,
                beginAtZero: true,
                title: { display: true, text: 'Number of Employees' }
            }
        }
    };
    return (
        <div className="mt-4">
           <h4 className="text-muted text-sm font-semibold">Attendance Report</h4>
            <label htmlFor="viewSelect">Select View:&nbsp;</label>
            <select
                id="viewSelect"
                className='chart-dropdown'
                value={view}
                onChange={(e) => setView(e.target.value)}
                disabled={loading}
            >
                <option value="month">Month</option>
                <option value="week">Week</option>
            </select>

            {loading && (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading attendance data...</p>
                </div>
            )}

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            {!loading && !error && data.datasets && data.datasets.length > 0 && (
                <div>
                    <Bar data={data} options={options} height={100} />
                </div>
            )}
        </div>
    )
}

export default AttendanceReport